/**
 * Reading and writing a platform flag's value, without guessing at its type.
 *
 * `platform_flags.value` is `jsonb`, so a row can hold anything, and today the
 * table holds two shapes: JSON booleans (`screening.enforce`, `signup.open`,
 * `mcp.write_tools`, `public_mode.require_screening`) and a JSON string
 * (`screening.engine`, which is `"none"` or `"clamav"`).
 *
 * The console edits a value in the shape it already has rather than parsing
 * whatever an operator types as JSON. That matters for exactly one flag and it
 * is the important one: typing `true` into `screening.engine` must send the
 * string `"true"`, which the platform reads as "not an engine", and never the
 * boolean `true`, which would be a value nothing can interpret sitting in the
 * row that decides whether uploads are scanned.
 *
 * Pure, and in its own module rather than inside the screen, so the rules can
 * be tested without a browser.
 */

export type FlagKind = "boolean" | "string" | "number" | "json";

/** The shape a flag's current value is in, which is the shape a write keeps. */
export function flagKind(value: unknown): FlagKind {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "string") return "string";
  if (typeof value === "number") return "number";
  return "json";
}

/** The value as an operator should read it in a table cell. */
export function formatFlagValue(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

/** The value as it should appear in the edit field, ready to be changed. */
export function editableFlagValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  // `undefined` is not JSON, and a flag row cannot hold it. Guarded anyway,
  // because an empty field is a better thing to show than the word "undefined".
  return value === undefined ? "" : JSON.stringify(value);
}

export type ParsedFlag = { ok: true; value: unknown } | { ok: false; problem: string };

/**
 * What to send for the text an operator typed, given the shape the flag is in.
 *
 * A string flag keeps every character it was given, trimmed only of the
 * whitespace a paste brings with it. Anything else is refused with a message
 * rather than coerced, because a flag written in the wrong shape is a kill
 * switch that reads as unset.
 */
export function parseFlagValue(kind: FlagKind, text: string): ParsedFlag {
  const trimmed = text.trim();

  if (kind === "string") {
    if (trimmed === "") return { ok: false, problem: "Give a value." };
    // `docs/runbooks/clamav.md` writes the value the way the database holds it,
    // `"clamav"` quotes and all, and an operator following it into this field
    // would store a five-letter word wrapped in quote characters, which reads
    // as no engine at all. Refused rather than quietly unwrapped: stripping
    // characters somebody typed is how the next surprise gets built.
    if (trimmed.length > 1 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
      return { ok: false, problem: "Type the value itself, without the quotes." };
    }
    return { ok: true, value: trimmed };
  }

  if (kind === "number") {
    const parsed = Number(trimmed);
    if (trimmed === "" || !Number.isFinite(parsed)) {
      return { ok: false, problem: "This flag holds a number." };
    }
    return { ok: true, value: parsed };
  }

  if (kind === "boolean") {
    if (trimmed === "true") return { ok: true, value: true };
    if (trimmed === "false") return { ok: true, value: false };
    return { ok: false, problem: "This flag holds true or false." };
  }

  try {
    return { ok: true, value: JSON.parse(trimmed) as unknown };
  } catch {
    return { ok: false, problem: "This flag holds JSON, and that is not valid JSON." };
  }
}
